import { useLocation } from "wouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Radar, Zap } from "lucide-react";

export interface OutOfCreditsDetails {
  message?: string;
  balance?: number;
  cost?: number;
  gridSize?: number;
  tier?: string;
  resetAt?: string | null;
}

interface OutOfCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details?: OutOfCreditsDetails | null;
}

function formatResetDate(resetAt?: string | null): string | null {
  if (!resetAt) return null;
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export function OutOfCreditsDialog({ open, onOpenChange, details }: OutOfCreditsDialogProps) {
  const [, navigate] = useLocation();
  const balance = details?.balance ?? 0;
  const cost = details?.cost ?? 0;
  const shortBy = Math.max(0, cost - balance);
  const resetLabel = formatResetDate(details?.resetAt);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md" data-testid="dialog-out-of-credits">
        <AlertDialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-[#F6642A]/10 flex items-center justify-center mb-2">
            <Radar className="h-6 w-6 text-[#F6642A]" />
          </div>
          <AlertDialogTitle className="text-center text-2xl" data-testid="text-out-of-credits-title">
            Out of scan credits
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center" data-testid="text-out-of-credits-description">
            You need more GeoGrid credits to run this scan. Top up your balance to keep tracking
            your map-pack rankings and watching the competition.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid grid-cols-3 gap-3 py-4">
          <div className="rounded-lg border bg-muted/30 p-3 text-center" data-testid="stat-balance">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Balance</div>
            <div className="text-2xl font-bold mt-1">{balance.toLocaleString()}</div>
          </div>
          <div className="rounded-lg border bg-muted/30 p-3 text-center" data-testid="stat-needed">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Needed</div>
            <div className="text-2xl font-bold mt-1">{cost.toLocaleString()}</div>
          </div>
          <div
            className="rounded-lg border-2 border-[#F6642A]/40 bg-[#F6642A]/5 p-3 text-center"
            data-testid="stat-short"
          >
            <div className="text-xs text-[#F6642A] uppercase tracking-wide font-semibold">
              Short by
            </div>
            <div className="text-2xl font-bold mt-1 text-[#F6642A]">
              {shortBy.toLocaleString()}
            </div>
          </div>
        </div>

        {resetLabel && (
          <div
            className="text-xs text-center text-muted-foreground border-t pt-3"
            data-testid="text-reset-info"
          >
            Your monthly tier allowance refreshes on{" "}
            <span className="font-medium text-foreground">{resetLabel}</span>.
          </div>
        )}

        <AlertDialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2">
          <AlertDialogAction
            className="w-full bg-[#F6642A] hover:bg-[#F6642A]/90 text-white border-0"
            onClick={() => {
              onOpenChange(false);
              navigate("/scan-credits");
            }}
            data-testid="button-buy-credits"
          >
            <Zap className="h-4 w-4 mr-2" />
            Refill credits now
          </AlertDialogAction>
          <AlertDialogCancel
            className="w-full mt-0"
            data-testid="button-dismiss-out-of-credits"
          >
            Maybe later
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Parse an apiRequest error to detect a 402 Payment Required response from
 * the GeoGrid scan endpoint and extract the credit details. Returns null if
 * the error is not a 402 / not parseable.
 *
 * apiRequest throws errors of the form `Error("${status}: ${responseBody}")`
 * where responseBody is the raw text of the response.
 */
export function parseOutOfCreditsError(err: unknown): OutOfCreditsDetails | null {
  if (!(err instanceof Error)) return null;
  if (!err.message.startsWith("402:")) return null;
  const bodyStr = err.message.slice(4).trim();
  try {
    const body = JSON.parse(bodyStr);
    if (body && typeof body === "object") {
      return {
        message: typeof body.message === "string" ? body.message : undefined,
        balance: typeof body.balance === "number" ? body.balance : undefined,
        cost: typeof body.cost === "number" ? body.cost : undefined,
        gridSize: typeof body.gridSize === "number" ? body.gridSize : undefined,
        tier: typeof body.tier === "string" ? body.tier : undefined,
        resetAt: typeof body.resetAt === "string" ? body.resetAt : null,
      };
    }
  } catch {
    // Fall through — return a minimal details object so the dialog still shows.
  }
  return { message: bodyStr };
}
